import { useCallback, useRef, useState } from 'react'
import { APIResponse } from '../../../../lib/commons/api'
import store from '../../../../store'
import {
  requireSave,
  doNotRequireSave,
} from '../../../../store/requiredSaveData'
import {
  getPrograms,
  updateProgramsBatch,
} from '../../../../infrastructure/api/programs'
import {
  ProgramRow,
  buildEditedDelta,
  buildCreateInput,
  createNewProgramRow,
} from '../model/programRow'

type UseProgramDataReturn = {
  data: ProgramRow[]
  loading: boolean
  fetchPrograms: () => Promise<void>
  hasChanged: () => boolean
  save: () => Promise<APIResponse>
  addRow: () => void
  deleteRows: (uuids: string[]) => void
}

const cloneByUuid = (rows: ProgramRow[]): Map<string, ProgramRow> =>
  new Map(rows.map(row => [row.uuid, structuredClone(row)]))

export const useProgramData = (): UseProgramDataReturn => {
  const [data, setData] = useState<ProgramRow[]>([])
  const [loading, setLoading] = useState(false)
  const requestIdRef = useRef(0)
  const dataRef = useRef<ProgramRow[]>([])
  const originalRef = useRef<Map<string, ProgramRow>>(new Map())
  const deletedUuidsRef = useRef<string[]>([])
  dataRef.current = data

  const fetchPrograms = useCallback(async () => {
    const requestId = ++requestIdRef.current
    setLoading(true)
    try {
      const response = await getPrograms()
      if (requestId !== requestIdRef.current) return
      const rows: ProgramRow[] = response.json ?? []
      originalRef.current = cloneByUuid(rows)
      deletedUuidsRef.current = []
      setData(rows)
      store.dispatch(doNotRequireSave())
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false)
      }
    }
  }, [])

  const addRow = useCallback(() => {
    setData(current => {
      const existingCodes = current.map(row => row.code).filter(Boolean)
      return [...current, createNewProgramRow(existingCodes)]
    })
    store.dispatch(requireSave())
  }, [])

  const deleteRows = useCallback((uuids: string[]) => {
    const targets = new Set(uuids)
    deletedUuidsRef.current = [
      ...deletedUuidsRef.current,
      ...uuids.filter(uuid => originalRef.current.has(uuid)),
    ]
    setData(current => current.filter(row => !targets.has(row.uuid)))
    store.dispatch(requireSave())
  }, [])

  const hasChanged = useCallback(
    () =>
      deletedUuidsRef.current.length > 0 ||
      dataRef.current.some(row => row.added || row.edited),
    []
  )

  const save = useCallback((): Promise<APIResponse> => {
    const rows = dataRef.current
    const added = rows.filter(row => row.added).map(buildCreateInput)
    const edited = rows
      .filter(
        row => !row.added && row.edited && originalRef.current.has(row.uuid)
      )
      .map(row => buildEditedDelta(row, originalRef.current.get(row.uuid)!))
    const deleted = deletedUuidsRef.current.map(uuid => ({ uuid }))
    return updateProgramsBatch({ added, edited, deleted })
  }, [])

  return {
    data,
    loading,
    fetchPrograms,
    hasChanged,
    save,
    addRow,
    deleteRows,
  }
}
